export const testDateFormat = (value: string | undefined) => {
  return value && /^\d{4}(-\d\d(-\d\d(T\d\d:\d\d(:\d\d)?(\.\d+)?(([+-]\d\d:\d\d)|Z)?)?)?)?$/i.test(value);
};

export const getDateFormatFromValues = (year: string | number, month: string | number, day: string | number) => {
  return `${year.toString()}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};

export const getDateTimeString = (date: Date): string => {
  const pad = (num: number): string => (num < 10 ? `0${num}` : `${num}`);
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const timezoneOffset = date.getTimezoneOffset();
  const offsetSign = timezoneOffset > 0 ? '-' : '+';
  const absOffset = Math.abs(timezoneOffset);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMinutes = pad(absOffset % 60);
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

export const getDateFormat = (date: Date) => {
  return `${date.getFullYear().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}`;
};

export const checkDateParts = (date: any) => {
  if (typeof date !== 'string') {
    return false;
  }

  const parts = date.split('-');

  if (!testDateFormat(date)) {
    return false;
  }

  if (
    Number(parts[1]) < 13 &&
    Number(parts[1]) > 0 &&
    Number(parts[2]) < 32 &&
    Number(parts[2]) > 0 &&
    Number(parts[0]) > 0
  ) {
    return true;
  }

  return false;
};

export const getDateParts = (date: string) => {
  const parts = date.split('-');

  return { year: parts[0], month: parts[1], day: parts[2] };
};

export const getTimeZone = (): string => {
  const date = new Date();
  const timeZoneName = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
  return timeZoneName;
};

type TimeFormat = 'short' | 'long';
type TimeZoneFormat = 'local' | 'utc';
type HourFormat = '12' | '24';
type DateFormat = 'long' | 'short';

interface DateTimeFormatOptions {
  timeFormat?: TimeFormat;
  timeZoneFormat?: TimeZoneFormat;
  hourFormat?: HourFormat;
  dateFormat?: DateFormat;
  showTime?: boolean;
}

export const formatDateTime = (date: string | Date, options: DateTimeFormatOptions = {}): string => {
  const { timeZoneFormat = 'local' } = options;
  const timestamp = new Date(date);

  const formattedDate = formatDate(timestamp, { timeZoneFormat });
  const formattedTime = formatTime(timestamp, {
    timeZoneFormat,
    hourFormat: timeZoneFormat === 'utc' ? '24' : '12',
    timeFormat: 'short',
  });

  return `${formattedDate} ${formattedTime}`;
};

export const getTimePassed = (time: string | Date): string => {
  const currentTime = new Date();
  const eventTime = new Date(time);
  const timeDiff = currentTime.getTime() - eventTime.getTime();
  const seconds = Math.floor(timeDiff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 365) {
    const years = Math.floor(days / 365);
    return `${years}y ago`;
  }
  if (days > 7) {
    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
  }
  if (days > 0) {
    return `${days}d ago`;
  }
  if (hours > 0) {
    return `${hours}h ago`;
  }
  if (minutes > 0) {
    return `${minutes}m ago`;
  }
  return `${seconds}s ago`;
};

export type FormattedDateFormat =
  | 'time-passed'
  | 'mm/dd/yyyy'
  | 'mm-dd-yyyy'
  | 'month year'
  | 'month day'
  | 'month day hh:mm'
  | 'month day hh:mm AM/PM'
  | 'day month year'
  | 'mm/dd/yyy hh:mm'
  | 'mm/dd/yyy hh:mm AM/PM'
  | 'mm dd, yyyy hh:mm'
  | 'mm dd, yyyy hh:mm AM/PM';

export const getFormattedDate = (timestamp: Date | string, format: FormattedDateFormat) => {
  const date = new Date(timestamp);

  switch (format) {
    case 'time-passed':
      return getTimePassed(timestamp);
    case 'mm/dd/yyyy':
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    case 'mm-dd-yyyy':
      return date
        .toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })
        .replaceAll('/', '-');
    case 'month year':
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    case 'month day':
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      });
    case 'month day hh:mm':
      return `${date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}`;
    case 'month day hh:mm AM/PM':
      return `${date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })}`;
    case 'day month year':
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    case 'mm/dd/yyy hh:mm':
      return `${date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}`;
    case 'mm/dd/yyy hh:mm AM/PM':
      return `${date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })}`;
    case 'mm dd, yyyy hh:mm':
      return `${date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}`;
    case 'mm dd, yyyy hh:mm AM/PM':
      return `${date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })}`;
    default:
      return getTimePassed(timestamp);
  }
};

interface DateFormatOptions {
  dateFormat?: DateFormat;
  timeZoneFormat?: TimeZoneFormat;
}

export const formatDate = (date: string | Date, options: DateFormatOptions = {}): string => {
  const { dateFormat = 'long', timeZoneFormat = 'local' } = options;
  const timestamp = new Date(date);

  const month = timestamp.toLocaleString('en-US', {
    month: dateFormat === 'long' ? 'long' : 'short',
    timeZone: timeZoneFormat === 'utc' ? 'UTC' : undefined,
  });
  const day = timeZoneFormat === 'utc' ? timestamp.getUTCDate() : timestamp.getDate();
  const year = timeZoneFormat === 'utc' ? timestamp.getUTCFullYear() : timestamp.getFullYear();

  return `${month} ${day}, ${year}`;
};

interface TimeFormatOptions {
  timeFormat?: TimeFormat;
  timeZoneFormat?: TimeZoneFormat;
  hourFormat?: HourFormat;
}

export const formatTime = (date: string | Date, options: TimeFormatOptions = {}): string => {
  const { timeFormat = 'short', timeZoneFormat = 'local', hourFormat = '12' } = options;
  const timestamp = new Date(date);

  let hours = timeZoneFormat === 'utc' ? timestamp.getUTCHours() : timestamp.getHours();
  const minutes =
    timeZoneFormat === 'utc'
      ? timestamp.getUTCMinutes().toString().padStart(2, '0')
      : timestamp.getMinutes().toString().padStart(2, '0');

  const timeZoneOffset = timestamp.getTimezoneOffset();
  const timeZoneOffsetHours = Math.abs(Math.floor(timeZoneOffset / 60))
    .toString()
    .padStart(2, '0');
  const timeZoneOffsetMinutes = (Math.abs(timeZoneOffset) % 60).toString().padStart(2, '0');
  const timeZoneSign = timeZoneOffset >= 0 ? '-' : '+';

  let formattedHours = hours.toString();
  let ampm = '';

  if (hourFormat === '12') {
    ampm = hours >= 12 ? ' PM' : ' AM';
    hours = hours % 12 || 12;
    formattedHours = hours.toString();
  } else {
    formattedHours = hours.toString().padStart(2, '0');
  }

  const formattedTime = `${formattedHours}:${minutes}${ampm}`;
  const timeZoneSuffix =
    timeZoneFormat === 'utc'
      ? ` ${timeZoneSign}${timeZoneOffsetHours}:${timeZoneOffsetMinutes} UTC`
      : ` ${getTimeZone()}`;

  if (timeFormat === 'short') {
    return `${formattedTime}${timeZoneSuffix}`;
  }

  return `${formattedTime} ${timeZoneSign}${timeZoneOffsetHours}:${timeZoneOffsetMinutes}${timeZoneSuffix}`;
};
